import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const CX = 200;
const CY = 190;
const polar = (deg, r) => {
  const a = (deg * Math.PI) / 180;
  return [CX + r * Math.sin(a), CY - r * Math.cos(a)];
};
const arc = (from, to, r) => {
  const [x1, y1] = polar(from, r);
  const [x2, y2] = polar(to, r);
  return `M ${x1} ${y1} A ${r} ${r} 0 ${to - from > 180 ? 1 : 0} 1 ${x2} ${y2}`;
};

// 9 labelled ticks with 4 minor ticks between each: -120deg ... +120deg
const TICKS = Array.from({ length: 33 }, (_, i) => ({
  deg: -120 + i * 7.5,
  major: i % 4 === 0,
}));

export default function NotFoundPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return (
    <section className= "pg-error" >
      <div className="px-6 py-16 sm:px-10 lg:py-24 ">
      <style>{`
        @keyframes nf-sweep {
          0%   { transform: rotate(-120deg); }
          40%  { transform: rotate(132deg); }
          55%  { transform: rotate(100deg); }
          70%  { transform: rotate(118deg); }
          100% { transform: rotate(112deg); }
        }
        @keyframes nf-light {
          0%, 100% { opacity: 1; }
          50%      { opacity: .25; }
        }
        .nf-needle { transform-box: view-box; transform-origin: ${CX}px ${CY}px; transform: rotate(112deg); animation: nf-sweep 2.2s cubic-bezier(.3,.7,.3,1) both; }
        .nf-light  { animation: nf-light 1.4s ease-in-out 2.2s infinite; }
        @media (prefers-reduced-motion: reduce) {
          .nf-needle, .nf-light { animation: none; }
        }
      `}</style>

      <div className="mx-auto grid w-full max-w-5xl items-center gap-12 md:grid-cols-[1fr_1.1fr] md:gap-16">
        <div>
          <h1 className="font-heading text-4xl font-bold leading-tight text-slate-900 sm:text-5xl">
            You&apos;ve gone off the dial.
          </h1>
          <p className="mt-4 max-w-md text-base leading-relaxed text-slate-600 sm:text-lg">
            There&apos;s no page at{" "}
            <code className="break-all rounded bg-slate-100 px-1.5 py-0.5 text-[0.9em] text-[#c1392a]">
              {pathname}
            </code>
            . It may have been moved, or the link may be mistyped.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/"
              className="themed inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-bold text-white shadow-lg shadow-[#c1392a]/20 transition-all hover:bg-[#a62f24] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#c1392a]"
            >
              Back to homepage
            </Link>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center justify-center rounded-full border border-slate-300 px-6 py-3 text-sm font-bold text-slate-700 transition-colors hover:border-slate-900 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#c1392a]"
            >
              Go to previous page
            </button>
          </div>
        </div>

        {/* Instrument cluster */}
        <div
          role="img"
          aria-label="Car speedometer with the needle pushed past the limit, reading 404"
          className="rounded-[2.5rem] bg-slate-900 p-3 shadow-2xl shadow-slate-900/30 ring-1 ring-slate-700"
        >
          <div className="rounded-[2rem] bg-gradient-to-b from-slate-950 to-slate-900 px-4 pb-6 pt-8 ring-1 ring-black">
            <svg viewBox="0 0 400 290" className="w-full" aria-hidden="true">
              {/* track and redline */}
              <path d={arc(-120, 120, 150)} fill="none" stroke="#273041" strokeWidth="10" strokeLinecap="round" />
              <path d={arc(84, 120, 150)} fill="none" stroke="#c1392a" strokeWidth="10" strokeLinecap="round" />

              {/* ticks */}
              {TICKS.map(({ deg, major }) => {
                const [x1, y1] = polar(deg, major ? 122 : 130);
                const [x2, y2] = polar(deg, 138);
                return (
                  <line
                    key={deg}
                    x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke={deg >= 84 ? "#e0685a" : "#8b97ad"}
                    strokeWidth={major ? 3 : 1.5}
                  />
                );
              })}

              {/* digital readout */}
              <text x={CX} y={CY - 18} textAnchor="middle" fill="#f1f5f9" fontSize="64" fontWeight="700" letterSpacing="4" fontFamily="inherit">
                404
              </text>
              <text x={CX} y={CY + 8} textAnchor="middle" fill="#8b97ad" fontSize="14" fontFamily="inherit">
                page not found
              </text>

              {/* needle */}
              <g className="nf-needle">
                <line x1={CX} y1={CY + 18} x2={CX} y2={CY - 128} stroke="#ff5a47" strokeWidth="4" strokeLinecap="round" />
                <circle cx={CX} cy={CY} r="12" fill="#0f172a" stroke="#ff5a47" strokeWidth="3" />
              </g>

              {/* warning lights */}
              <g className="nf-light" transform="translate(172 246)">
                {/* check-engine style icon */}
                <path
                  d="M4 8h5l2-3h8l2 3h4v3h3v8h-3v3h-5l-2 2H11l-2-2H4z"
                  fill="none" stroke="#f5a524" strokeWidth="2" strokeLinejoin="round"
                />
              </g>
            </svg>
          </div>
        </div>
      </div>
      </div>
    </section>
  );
}